import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box } from "@mui/material";
import { useFormikContext } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { useDownloadExcel } from "react-export-table-to-excel";
import { useTranslation } from "react-i18next";
import { FaLongArrowAltLeft } from "react-icons/fa";
import { useSelector } from "react-redux";
import { ResponsiveContainer } from "recharts";
import { useEditorContext } from "../../../contexts/EditorContext";
import {
  getCounts,
  getText,
  removeAccents,
} from "../../../pages/postEditor/seoAnalyzer/helper";
import {
  calculateOverOptimizedScore,
  calculateWellOptimizedScore,
  keywordCount,
} from "../../../pages/postEditor/seoAnalyzer/semanticHelper";
import { startSemanticAnalysis } from "../../../pages/postEditor/seoAnalyzer/semantics";
import { setActivePanel } from "../../../redux/slices/panelSlice";
import { dispatch } from "../../../redux/store";
import { LaunchAnalysisBtn } from "../Others";

const Semantics = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state?.auth.userInfo);
  const competitorResults = useSelector(
    (state) => state.competitorAnalysis.data?.data
  );
  const [analysis, setAnalysis] = useState();
  const [showModal, setShowModal] = useState(false);
  const [semantic_view, setSemanticView] = useState(false);
  const [competitorScoreView, setCompetitorScoreView] = useState(false);
  const [keywords_analysis, setKeywords_analysis] = useState();
  const formikProps = useFormikContext();
  const tableRef = useRef(null);
  const editor = useEditorContext();
  const [cursorPosition, setCursorPosition] = useState(null);
  const wordCount =
    getCounts(
      getText(
        formikProps.values.h1_title.trim() +
          formikProps.values.introduction.trim() +
          formikProps.values.content.trim() +
          formikProps.values.conclusion.trim()
      )
        .toLowerCase()
        .trim()
    ).words || formikProps.values.nb_words;

  // Export to excel
  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: "Keywords-Repartition",
    sheet: "Keywords-Repartition",
  });

  useEffect(() => {
    // Run immediately on first mount
    const initialAnalysis = startSemanticAnalysis(
      formikProps,
      competitorResults
    );
    setAnalysis(initialAnalysis);
  }, []); // only once on mount

  useEffect(() => {
    // Debounce effect for typing
    const handler = setTimeout(() => {
      const analyze = startSemanticAnalysis(formikProps, competitorResults);
      setAnalysis(analyze);
    }, 3000); // wait 3s after last change

    return () => {
      clearTimeout(handler); // clear if typing continues
    };
  }, [formikProps.values, competitorResults]);

  // Count Keywords
  const handleCountKeyword = (keyword) => {
    const text =
      getText(formikProps.values.introduction.trim()).toLowerCase().trim() +
      getText(formikProps.values.content.trim()).toLowerCase().trim() +
      getText(formikProps.values.conclusion.trim()).toLowerCase().trim();
    return keywordCount(removeAccents(keyword), removeAccents(text));
  };

  // Set View Competitors Score Graph
  const handleCompetitorScoreView = (value) => {
    setCompetitorScoreView(value);
  };

  // Calculate well optimized score
  const wellOptimizedScore = useMemo(() => {
    return calculateWellOptimizedScore(keywords_analysis, formikProps);
  }, [keywords_analysis]);

  // Calculate over optimized score
  const overOptimizedScore = useMemo(() => {
    return calculateOverOptimizedScore(keywords_analysis, formikProps);
  }, [keywords_analysis]);

  // Open/Close Modal
  const handleModal = (data) => {
    if (data.type === "semantics") {
      setShowModal(data.show);
      setKeywords_analysis(data.keywords);
      setSemanticView(data.semantics_view);
    }
  };

  // Close Panel
  const handleClose = () => {
    dispatch(setActivePanel(null));

    editor.commands.toggleHighlighting(false);
    editor.commands.highlightKeywords();
  };

  // View Graph/Table
  const handleSemanticView = (value) => {
    setSemanticView(value);
  };

  // Keywords Score Chart Options
  const getOptimizationChartOptions = () => {
    const secondary = keywords_analysis?.secondary_keywords || [];
    return {
      chart: {
        width: "100%",
        parentHeightOffset: -10,
        type: "line",
        height: "95%",
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        pan: {
          enabled: false,
        },
      },
      grid: {
        show: true,
        borderColor: "transparent",
        row: {
          colors: ["#df9190"],
          opacity: 1,
        },
      },
      stroke: {
        curve: "straight",
        width: [0, 0, 1],
      },
      markers: {
        size: [0, 0, 4],
        colors: ["#A6BD87", "#8AA6B1", "#333333"],
        strokeColors: ["#A6BD87", "#8AA6B1", "#333333"],
        shape: "circle",
        offsetX: 0,
        offsetY: 0,
      },
      fill: {
        type: "solid",
        colors: ["#A6BD87", "#8AA6B1", "#333333"],
        opacity: [1, 1, 1],
      },
      labels: secondary.map((item) => item?.keywords),
      yaxis: {
        title: {
          text: t("Optimization", "Optimisation"),
        },
      },
      legend: {
        show: false, // On masque la légende par défaut
      },
      // Plus d'annotation
      xaxis: {
        crosshairs: {
          show: true,
          width: 1,
          position: "back",
          opacity: 0.7,
          stroke: {
            color: "#333",
            width: 2,
            dashArray: 0,
          },
        },
      },
      tooltip: {
        shared: true,
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          if (!secondary[dataPointIndex]) return "";
          const item = secondary[dataPointIndex];
          const low = Math.round((item.low / 100) * wordCount);
          const high = Math.round((item.high / 100) * wordCount);
          const yourContent = handleCountKeyword(item.keywords, formikProps);
          return `
            <div style="background:#fff;border-radius:8px;box-shadow:0 2px 8px #0001;overflow:hidden;font-family:inherit;min-width:180px">
              <div style="background:#f5f6f7;padding:8px 12px;font-weight:600;border-bottom:1px solid #eee;">${
                item.keywords
              }</div>
              <div style="padding:10px 12px 6px 12px;">
                <div style="display:flex;align-items:center;margin-bottom:6px"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#FE3E40;margin-right:8px;"></span><span style="font-size:14px;">${t(
                  "Suroptimisé",
                  "Suroptimisé"
                )}:</span><span style="font-weight:700;margin-left:6px;">> ${high}</span></div>
                <div style="display:flex;align-items:center;margin-bottom:6px"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#A6BD87;margin-right:8px;"></span><span style="font-size:14px;">${t(
                  "Bien optimisé",
                  "Bien optimisé"
                )}:</span><span style="font-weight:700;margin-left:6px;">${low} - ${high}</span></div>
                <div style="display:flex;align-items:center;margin-bottom:6px"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#8AA6B1;margin-right:8px;"></span><span style="font-size:14px;">${t(
                  "Sous-optimisé",
                  "Sous-optimisé"
                )}:</span><span style="font-weight:700;margin-left:6px;"> < ${low}</span></div>
                <div style="display:flex;align-items:center;"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#333;margin-right:8px;"></span><span style="font-size:14px;">${t(
                  "Votre contenu",
                  "Votre contenu"
                )}:</span><span style="font-weight:700;margin-left:6px;">${yourContent}</span></div>
              </div>
            </div>
          `;
        },
      },
    };
  };

  const getOptimizationChartSerires = () => {
    const secondary = keywords_analysis?.secondary_keywords || [];
    return [
      {
        name: t("Well-optimized", "Bien optimisé"),
        type: "area",
        data: secondary.map((item) => ({
          x: item?.keywords,
          y: Math.round((item?.high / 100) * wordCount),
        })),
      },
      {
        name: t("Underoptimized", "Sous-optimisé"),
        type: "area",
        data: secondary.map((item) => ({
          x: item?.keywords,
          y: Math.round((item?.low / 100) * wordCount),
        })),
      },
      {
        name: t("Your-Content", "Votre contenu"),
        type: "line",
        data: secondary.map((item) => ({
          x: item?.keywords,
          y: handleCountKeyword(item?.keywords, formikProps),
        })),
      },
    ];
  };

  // Optimization Chart Options
  const getOptimizationScoreChartOptions = () => {
    return {
      chart: {
        parentHeightOffset: -10,
        type: "bar",
        height: "95%",
        width: "100%",
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        pan: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
      },
      markers: {
        size: 0,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          borderRadius: 5,
          borderRadiusApplication: "end",
        },
      },
      fill: {
        type: "solid",
        colors: ["#AAEF99", "#DD999A"],
        opacity: 1,
      },
      labels: keywords_analysis?.scores?.map((item) => {
        return item?.page;
      }),
      stroke: {
        colors: ["transparent"],
        width: 1,
      },
      yaxis: {
        labels: {
          formatter: (val) => `${val}%`,
        },
      },
    };
  };
  //  Optimization Chart series values
  const getOptimizationScoreChartSeries = () => {
    return [
      {
        name: t("Well-optimized", "Well-optimized"),
        type: "bar",
        data: [
          {
            x: t("Your-page", "Your page"),
            y: wellOptimizedScore,
          },
        ].concat(
          keywords_analysis?.scores?.map((item) => {
            return {
              x: item?.page,
              y: item?.optimization_score,
            };
          })
        ),
      },
      {
        name: t("Overoptimized", "Overoptimized"),
        type: "bar",
        data: [
          {
            x: t("Your-page", "Your page"),
            y: overOptimizedScore,
          },
        ].concat(
          keywords_analysis?.scores?.map((item) => {
            return {
              x: item?.page,
              y: item?.overoptimization_score,
            };
          })
        ),
      },
    ];
  };

  return (
    <Box
      sx={{
        position: "fixed",
        overflow: "auto",
        height: { md: "100vh", xs: "100%" },
        width: "auto",
        display: "flex",
        flexDirection: { md: "row", xs: "column" },
        mt: { md: "48px !important", xs: "unset !important" },

        top: 0,
        bgcolor: showModal ? "#000000a1" : "transparent",
        zIndex: 9999,
      }}
      className={`${showModal ? "left-0" : "right-0"}`}
    >
      <Box
        className={`${showModal ? "flex" : "hidden"}`}
        sx={{
          width: {
            lg: "calc(100vw - 500px)",
            md: "calc(100vw - 350px)",
            xs: "100%",
          },

          justifyContent: "center",
          height: "auto",
          //overflow: { md: 'auto', xs: 'unset' },
        }}
      >
        <Box
          sx={{
            width: "100%",
            my: { md: "unset", xs: "40px" },
          }}
        >
          <div className="h-screen flex">
            <Box
              sx={{
                maxHeight: "95%",
                height: "90%",
                mx: "auto",
                mt: 4,
                bgcolor: "white",
                maxWidth: "95%",
                width: "90%",
                borderRadius: 4,
                overflow: "hidden",
                display: "flex",

                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 3,
                  position: "relative",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                <p className="font-semibold">
                  {t("Keywords-repartition", "Keywords repartition")}
                </p>
                <Box
                  onClick={() => {
                    handleModal({
                      type: "semantics",
                      keywords: {
                        scores: [],
                        secondary_keywords: [],
                        semantics_view: keywords_analysis?.semantics_view,
                      },
                      show: false,
                    });
                  }}
                  sx={{
                    position: "absolute",
                    cursor: "pointer",
                    right: 0,
                    top: 0,
                    p: 2,
                  }}
                >
                  <Close sx={{ color: "black", fontSize: 32 }} />
                </Box>
              </Box>
              <Box
                sx={{
                  bgcolor: "white",
                  px: 3,
                  py: 2,
                  flex: 1,
                  overflow: "auto",
                }}
              >
                {competitorScoreView ? (
                  <>
                    <button
                      className="flex items-center gap-3 my-3 font-bold"
                      onClick={() => {
                        handleCompetitorScoreView(false);
                      }}
                    >
                      <FaLongArrowAltLeft></FaLongArrowAltLeft>
                      {t("go-back-to-analysis", "Go back to analysis")}
                    </button>
                    <ResponsiveContainer width="100%" height={500}>
                      <Chart
                        options={getOptimizationScoreChartOptions()}
                        series={getOptimizationScoreChartSeries()}
                        width="100%"
                      />
                    </ResponsiveContainer>
                  </>
                ) : (
                  <>
                    <div className="flex xl:flex-row flex-col px-5 pb-3 flex-wrap justify-center  lg:justify-between gap-2 items-center">
                      <div>
                        <p className="text-sm">
                          {t("Optimization-Score", "Optimization Score")} :{" "}
                          <span
                            className={`${
                              wellOptimizedScore >= 75
                                ? "text-green-500"
                                : wellOptimizedScore < 75 &&
                                  wellOptimizedScore >= 35
                                ? "text-yellow-500"
                                : "text-red-600"
                            }`}
                          >
                            {" "}
                            {wellOptimizedScore}%{" "}
                          </span>
                        </p>
                      </div>
                      <div>
                        {" "}
                        <p className="text-sm">
                          {t(
                            "Overoptimization-Score",
                            "Overoptimization Score"
                          )}{" "}
                          :{" "}
                          <span
                            className={`${
                              100 - overOptimizedScore >= 75
                                ? "text-green-500"
                                : 100 - overOptimizedScore < 75 &&
                                  100 - overOptimizedScore >= 35
                                ? "text-yellow-500"
                                : "text-red-600"
                            }`}
                          >
                            {" "}
                            {overOptimizedScore}%
                          </span>
                        </p>
                      </div>
                      <div>
                        <button
                          className="border-solid border-2 border-black font-bold text-black text-center p-3 px-10 rounded 
                          hover:bg-black
                          hover:text-white
                          text-xs xl:text-sm
                          "
                          onClick={() => {
                            handleCompetitorScoreView(true);
                          }}
                        >
                          {t(
                            "View-competitors-scores",
                            "View competitors' scores"
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap justify-start md:justify-center gap-2 items-center">
                      <button
                        className={`${
                          semantic_view === "graph"
                            ? "bg-stone-400"
                            : "bg-stone-200 hover:bg-stone-300"
                        } text-black font-bold  w-full md:w-[48%] text-center text-sm p-3 rounded `}
                        onClick={() => {
                          handleSemanticView("graph");
                        }}
                      >
                        {t("Graph", "Graph")}
                      </button>
                      <button
                        className={`${
                          semantic_view !== "graph"
                            ? "bg-stone-400"
                            : "bg-stone-200 hover:bg-stone-300"
                        } font-bold text-black w-full md:w-[48%] text-center text-sm p-3 rounded `}
                        onClick={() => {
                          handleSemanticView("table");
                        }}
                      >
                        {t("Table", "Table")}
                      </button>
                    </div>
                    {semantic_view === "graph" ? (
                      <>
                        <div className="overflow-y-hidden">
                          <p className="text-md font-semibold mt-5 text-center">
                            {formikProps?.values?.seo_request}
                          </p>
                          <div>
                            <ResponsiveContainer width="100%" height={420}>
                              <Chart
                                options={{
                                  ...getOptimizationChartOptions(),
                                  grid: {
                                    ...(getOptimizationChartOptions().grid ||
                                      {}),
                                    padding: { bottom: 30 },
                                  },
                                  markers: {
                                    ...getOptimizationChartOptions().markers,
                                    size: [0, 0, 2],
                                  },
                                  xaxis: {
                                    ...getOptimizationChartOptions().xaxis,
                                    labels: {
                                      ...getOptimizationChartOptions().xaxis
                                        ?.labels,
                                      style: { fontSize: "9px" },
                                      rotate: -45,
                                      trim: false,
                                      hideOverlappingLabels: false,
                                      showDuplicates: false,
                                    },
                                  },
                                }}
                                series={getOptimizationChartSerires()}
                                width={900}
                              />
                            </ResponsiveContainer>
                          </div>
                        </div>
                        <div className="flex gap-6 mt-4 mb-2 justify-center">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-4 h-4 rounded-full"
                              style={{ background: "#FE3E40" }}
                            ></span>
                            <span className="text-xs font-semibold">
                              {t("Suroptimisé", "Suroptimisé")}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-4 h-4 rounded-full"
                              style={{ background: "#A6BD87" }}
                            ></span>
                            <span className="text-xs font-semibold">
                              {t("Bien optimisé", "Bien optimisé")}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-4 h-4 rounded-full"
                              style={{ background: "#8AA6B1" }}
                            ></span>
                            <span className="text-xs font-semibold">
                              {t("Sous-optimisé", "Sous-optimisé")}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-4 h-4 rounded-full"
                              style={{ background: "#333" }}
                            ></span>
                            <span className="text-xs font-semibold">
                              {t("Votre contenu", "Votre contenu")}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full mt-2">
                        <div className="flex flex-row justify-end my-3">
                          <LoadingButton
                            variant="contained"
                            onClick={onDownload}
                            className="!text-sm hover:!bg-blue-100 *: !bg-blue-200 disabled:!bg-slate-100 disabled:!text-transparent
                                                                          !text-white font-light px-5 py-[6px] rounded flex items-center !normal-case"
                          >
                            {t("export-to-csv", "Export to CSV")}
                          </LoadingButton>
                        </div>
                        <div className="overflow-x-auto">
                          <div className="max-h-[340px] md:max-h-[480px] overflow-y-auto">
                            <table
                              ref={tableRef}
                              className="min-w-full table-fixed border-collapse text-xs text-center"
                            >
                              <thead>
                                <tr className="font-bold">
                                  <td className="sticky top-0 z-10 bg-pink-900 border-b-[1px] rounded-tl-lg border-neutral-500 text-white py-2 px-4 min-w-[120px]">
                                    {t("Keywords", "Keywords")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-cyan-900 border-[1px] border-t-0 border-neutral-500 text-white py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Underoptimized", "Underoptimized")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-lime-700 border-[1px] border-t-0 border-neutral-500 text-white py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Well-optimized", "Well-optimized")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-red-700 border-[1px] border-t-0 border-neutral-500 text-white py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Overoptimized", "Overoptimized")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-sky-950 border-[1px] border-t-0 border-neutral-500 text-white py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Your_Page", "Your Page")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-stone-400 border-[1px] border-t-0 border-neutral-500 text-black py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Page_1", "Page 1")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-stone-400 border-[1px] border-t-0 border-neutral-500 text-black py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Page_2", "Page 2")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-stone-400 border-[1px] border-t-0 border-neutral-500 text-black py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Page_3", "Page 3")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-stone-400 border-[1px] border-t-0 border-neutral-500 text-black py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Page_4", "Page 4")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-stone-400 border-[1px] border-t-0 border-neutral-500 text-black py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Page_5", "Page 5")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-stone-400 border-[1px] border-t-0 border-neutral-500 text-black py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Page_6", "Page 6")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-stone-400 border-[1px] border-t-0 border-neutral-500 text-black py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Page_7", "Page 7")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-stone-400 border-[1px] border-t-0 border-neutral-500 text-black py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Page_8", "Page 8")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-stone-400 border-[1px] border-t-0 border-neutral-500 text-black py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Page_9", "Page 9")}
                                  </td>
                                  <td className="sticky top-0 z-10 bg-stone-400 border-b-[1px] rounded-tr-lg border-neutral-500 text-black py-2 px-4 min-w-[100px] whitespace-nowrap">
                                    {t("Page_10", "Page 10")}
                                  </td>
                                </tr>
                              </thead>
                              <tbody>
                                {keywords_analysis?.secondary_keywords &&
                                  keywords_analysis?.secondary_keywords?.map(
                                    (item, index) => {
                                      return (
                                        <tr
                                          className="font-bold"
                                          key={item.keywords}
                                        >
                                          <td
                                            className={`min-w-[120px] ${
                                              index + 1 ===
                                              keywords_analysis
                                                ?.secondary_keywords?.length
                                                ? "rounded-bl-lg  border-b-0"
                                                : " border-b-[1px] "
                                            } bg-pink-900/50 border-neutral-500 text-black py-2 px-4`}
                                          >
                                            {item.keywords}
                                          </td>
                                          <td
                                            className={`min-w-[100px] whitespace-nowrap ${
                                              index + 1 ===
                                              keywords_analysis
                                                ?.secondary_keywords?.length
                                                ? "border-b-0"
                                                : "  border-t-0 "
                                            } border-[1px] bg-cyan-900/50 border-neutral-500 text-black py-2 px-4`}
                                          >{`<${Math.round(
                                            (item.low / 100) * wordCount
                                          )}`}</td>
                                          <td
                                            className={`min-w-[100px] whitespace-nowrap ${
                                              index + 1 ===
                                              keywords_analysis
                                                ?.secondary_keywords?.length
                                                ? "border-b-0"
                                                : " border-t-0 "
                                            } border-[1px] bg-lime-700/50 border-neutral-500 text-black py-2 px-4`}
                                          >
                                            {`${Math.round(
                                              (item.low / 100) * wordCount
                                            )} - ${Math.round(
                                              (item.high / 100) * wordCount
                                            )}`.toString()}
                                          </td>
                                          <td
                                            className={`min-w-[100px] whitespace-nowrap ${
                                              index + 1 ===
                                              keywords_analysis
                                                ?.secondary_keywords?.length
                                                ? "border-b-0"
                                                : " border-t-0 "
                                            } border-[1px] bg-red-700/50 border-neutral-500 text-black py-2 px-4`}
                                          >{`> ${Math.round(
                                            (item.high / 100) * wordCount
                                          )}`}</td>
                                          <td
                                            className={`min-w-[100px] whitespace-nowrap ${
                                              index + 1 ===
                                              keywords_analysis
                                                ?.secondary_keywords?.length
                                                ? "border-b-0"
                                                : " border-t-0 "
                                            } border-[1px] ${
                                              handleCountKeyword(
                                                item.keywords
                                              ) == 0
                                                ? "bg-[#D6D3D1]"
                                                : handleCountKeyword(
                                                    item.keywords
                                                  ) <
                                                  Math.round(
                                                    (item.low / 100) * wordCount
                                                  )
                                                ? "bg-cyan-900/50"
                                                : handleCountKeyword(
                                                    item.keywords
                                                  ) >
                                                  Math.round(
                                                    (item.high / 100) *
                                                      wordCount
                                                  )
                                                ? "bg-red-700/50"
                                                : "bg-lime-700/50"
                                            } border-neutral-500 text-black py-2 px-4`}
                                          >
                                            {handleCountKeyword(item.keywords)}
                                          </td>
                                          {[...Array(10)].map((_, i) => (
                                            <td
                                              key={i}
                                              className={`min-w-[100px] whitespace-nowrap ${
                                                index + 1 ===
                                                keywords_analysis
                                                  ?.secondary_keywords?.length
                                                  ? "border-b-0"
                                                  : " border-t-0 "
                                              } border-[1px] ${
                                                item[`Page ${i + 1}`] == 0
                                                  ? "bg-[#D6D3D1]"
                                                  : item[`Page ${i + 1}`] <
                                                    item.low
                                                  ? "bg-cyan-900/50"
                                                  : item[`Page ${i + 1}`] >
                                                    item.high
                                                  ? "bg-red-700/50"
                                                  : "bg-lime-700/50"
                                              } border-neutral-500 text-black py-2 px-4`}
                                            >
                                              {Math.round(
                                                (item[`Page ${i + 1}`] / 100) *
                                                  competitorResults.analysis[i]
                                                    .content.nb_words
                                              )}
                                            </td>
                                          ))}
                                        </tr>
                                      );
                                    }
                                  )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </div>
        </Box>
      </Box>
      <Box
        sx={{
          backgroundColor: "#303539",
          height: "100vh",
          width: { lg: "500px", md: "350px", xs: "100%" },
          color: "white",
          pb: 5,
        }}
        className="overflow-y-scroll"
      >
        <Box
          sx={{
            backgroundColor: "#005e57",
            p: 2,
            position: "relative",
          }}
        >
          <Box
            onClick={handleClose}
            sx={{
              position: "absolute",
              cursor: "pointer",
              right: 0,
              top: 0,
              p: 2,
            }}
          >
            <Close sx={{ color: "white" }} />
          </Box>
          <h1 className="text-2xl mb-2">{t("Semantics", "Sémantique")}</h1>

          <p className="text-xs mb-2">
            {t(
              "tutorial-text-semantics",
              "La sémantique de son contenu, c’est à dire les mots et groupes de mots utilisés permettent à Google de savoir si votre contenu est pertinent. Utilisez les mêmes mots que vos concurrents et ajoutez-en de nouveaux avec l’IA"
            )}
          </p>

          {/* <p className="text-xs">
            {t("For-more-info:", "Pour plus d'infos :")}{" "}
            <span>
              <a className="text-blue-500 underline cursor-pointer">
                {
                  ("semantics-optimization-guide",
                  "Renforcer la sémantique en SEO : le Guide Complet 2024")
                }
              </a>
            </span>
          </p> */}
        </Box>

        <LaunchAnalysisBtn
          userInfo={userInfo}
          formikProps={formikProps}
          disabled={true}
          type={"semantics"}
          showModal={showModal}
          showModalHandle={handleModal}
          analysis={analysis}
        />
      </Box>
    </Box>
  );
};

export default Semantics;
